import { useId, useState, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { CloseOutline } from '@raresail/react-ionicons';

/**
 * Floating-label text field (Figma: `input-text-field`).
 *
 * A tall rounded card whose placeholder text sits centered inside the field
 * while empty and floats up to a small uppercase header once the field is
 * focused or filled. Generic by design — behavior like phone formatting or
 * dynamic labels belongs in wrapper components (see sign-in's AccountField).
 *
 * Slots/props beyond the standard input props:
 * - `label`      section label rendered above the field
 * - `labelEnd`   content right-aligned on the label row (e.g. a sign-in link)
 * - `overlay`    custom right-side content
 * - `clearable`  shows a clear (X) button when the field has a value;
 *                pair with `onClear`
 * - `ghost`      inline hint rendered after the typed value while the field is
 *                focused (e.g. the remainder of a phone mask).
 *                Controlled inputs only.
 * - `readOnly`   filled field with no focus, hover, selection or clear
 *                affordance
 * - `subtext`    message below the field; replaced by `error.message`
 */
export const TextField = forwardRef(
  (
    {
      label,
      labelEnd,
      placeholder,
      id,
      icon,
      overlay,
      clearable,
      onClear,
      ghost,
      subtext,
      subtextClassName,
      error,
      labelClassName,
      containerClassName,
      fieldClassName,
      className,
      disabled,
      value,
      defaultValue,
      onChange,
      readOnly,
      ...props
    },
    ref
  ) => {
    const genId = useId();
    const inputId = id || genId;
    // Track fill state for uncontrolled usage; controlled usage derives it
    const [uncontrolledFilled, setUncontrolledFilled] =
      useState(!!defaultValue);
    const [focused, setFocused] = useState(false);
    const filled = value !== undefined ? !!value : uncontrolledFilled;

    const handleChange = (e) => {
      setUncontrolledFilled(!!e.target.value);
      onChange?.(e);
    };

    return (
      <div className={twMerge('flex flex-col gap-2', containerClassName)}>
        {(!!label || !!labelEnd) && (
          <div className="flex items-center justify-between gap-2">
            {!!label && (
              <label
                htmlFor={inputId}
                className={twMerge(
                  'text-sm font-semibold text-base-content',
                  labelClassName
                )}
              >
                {label}
              </label>
            )}
            {labelEnd}
          </div>
        )}
        <div
          className={twMerge(
            // overflow-hidden clips the UA autofill fill (yellow on iOS) to
            // the rounded corners — it paints on the square input, not here.
            'relative flex items-stretch overflow-hidden rounded-2xl border border-beautifi-blue-100/75 bg-base-100 shadow-beautifi transition-colors',
            'focus-within:border-info',
            // Read-only fields are filled in for the user, so they drop every
            // focus/hover affordance.
            readOnly &&
              'bg-slate-blue-50 focus-within:border-beautifi-blue-100/75',
            error &&
              'border-beautifi-pink-900 focus-within:border-beautifi-pink-900',
            disabled && 'opacity-40',
            fieldClassName
          )}
        >
          {!!icon && (
            <div className="flex items-center ps-4 text-muted">{icon}</div>
          )}
          <div className="relative flex-1">
            <input
              {...props}
              id={inputId}
              ref={ref}
              disabled={disabled}
              readOnly={readOnly}
              tabIndex={readOnly ? -1 : props.tabIndex}
              value={value}
              defaultValue={defaultValue}
              onChange={handleChange}
              onFocus={(e) => {
                setFocused(true);
                props.onFocus?.(e);
              }}
              onBlur={(e) => {
                setFocused(false);
                props.onBlur?.(e);
              }}
              placeholder=" "
              aria-invalid={!!error || undefined}
              className={twMerge(
                'peer min-h-[68px] w-full bg-transparent px-4 pt-6 pb-2.5 text-base sm:text-sm text-base-content placeholder-transparent outline-none',
                error && 'text-beautifi-pink-900',
                readOnly && 'pointer-events-none select-none',
                className
              )}
            />
            {!!ghost && focused && value !== undefined && (
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 flex items-center whitespace-pre px-4 pt-6 pb-2.5 text-base sm:text-sm"
              >
                <span className="invisible">{value}</span>
                <span className="text-divider">{ghost}</span>
              </div>
            )}
            {!!placeholder && (
              <label
                htmlFor={inputId}
                className="pointer-events-none absolute start-4 top-1/2 -translate-y-1/2 text-sm font-bold uppercase tracking-wide text-divider transition-all
                  peer-focus:top-3 peer-focus:translate-y-0 peer-focus:text-xs
                  peer-[:not(:placeholder-shown)]:top-3 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-xs"
              >
                {placeholder}
              </label>
            )}
          </div>
          {clearable && filled && !disabled && !readOnly && (
            <button
              type="button"
              aria-label="Clear"
              // Keep focus in the input while clearing
              onMouseDown={(e) => e.preventDefault()}
              onClick={onClear}
              className="flex cursor-pointer items-center pe-4 text-muted"
            >
              <CloseOutline className="h-4 w-4" />
            </button>
          )}
          {!!overlay && <div className="flex items-center pe-4">{overlay}</div>}
        </div>
        {error?.message ? (
          <p className="-mt-1 px-4 text-xs italic text-error">
            {error.message}
          </p>
        ) : (
          !!subtext && (
            <div
              className={twMerge('px-1 text-sm text-muted', subtextClassName)}
            >
              {subtext}
            </div>
          )
        )}
      </div>
    );
  }
);

TextField.displayName = 'TextField';
